// src/hooks/useNotifications.ts
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { notificationsAPI, type Notification } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const POLL_INTERVAL_MS = 60_000;

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  fetchAll: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: number) => Promise<void>;
  removeBulk: (ids: number[]) => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextValue | undefined
>(undefined);

// Single shared source for the unread badge and inbox state, mounted once in
// App.tsx. Kept as .ts, so the Provider returns React.createElement, not JSX.
export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Lets in-flight requests check sign-in state when they resolve.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const fetchUnreadCount = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const count = await notificationsAPI.getUnreadCount();
      if (isAuthenticatedRef.current) setUnreadCount(count);
    } catch {
      // silent — badge simply won't update
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    // Optimistically clear the shared badge while the inbox opens.
    setUnreadCount(0);
    try {
      const data = await notificationsAPI.getAll();
      if (!isAuthenticatedRef.current) return;
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.isRead).length);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: number) => {
    await notificationsAPI.markRead(id);
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id
          ? { ...n, isRead: true, readAt: new Date().toISOString() }
          : n
      )
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await notificationsAPI.markAllRead();
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isRead: true, readAt: now }))
    );
    setUnreadCount(0);
  }, []);

  const remove = useCallback(async (id: number) => {
    await notificationsAPI.delete(id);
    setNotifications((prev) => {
      const removed = prev.find((n) => n.id === id);
      if (removed && !removed.isRead) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  const removeBulk = useCallback(async (ids: number[]) => {
    await notificationsAPI.deleteBulk(ids);
    setNotifications((prev) => {
      const removedUnread = prev.filter(
        (n) => ids.includes(n.id) && !n.isRead
      ).length;
      setUnreadCount((c) => Math.max(0, c - removedUnread));
      return prev.filter((n) => !ids.includes(n.id));
    });
  }, []);

  // Poll only while signed in (pausing when the tab is hidden). On sign-out,
  // reset state so nothing outlives the session.
  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications((prev) => (prev.length ? [] : prev));
      setUnreadCount(0);
      return undefined;
    }

    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, fetchUnreadCount]);

  const value: NotificationsContextValue = {
    notifications,
    unreadCount,
    loading,
    fetchAll,
    markRead,
    markAllRead,
    remove,
    removeBulk,
  };

  return React.createElement(
    NotificationsContext.Provider,
    { value },
    children
  );
};

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationsProvider'
    );
  }
  return context;
}
